<template>
  <div class="vv-bell" ref="root">
    <button
      class="vv-bell__btn"
      type="button"
      :aria-label="$t('notifications.title')"
      :aria-expanded="open ? 'true' : 'false'"
      @click="toggle"
    >
      <i class="material-icons">notifications</i>
      <span v-if="badge" class="vv-bell__badge" data-test="bell-badge">{{ badge }}</span>
    </button>

    <div v-if="open" class="vv-bell__panel" role="dialog" :aria-label="$t('notifications.title')">
      <div class="vv-bell__head">
        <b>{{ $t("notifications.title") }}</b>
        <button class="vv-bell__link" type="button" :disabled="!count || busy" @click="readAll">
          {{ $t("notifications.markAll") }}
        </button>
      </div>
      <div v-if="loading" class="vv-bell__state">{{ $t("notifications.loading") }}</div>
      <div v-else-if="error" class="vv-bell__state">{{ $t("notifications.error") }}</div>
      <div v-else-if="items.length === 0" class="vv-bell__state">{{ $t("notifications.empty") }}</div>
      <ul v-else class="vv-bell__list">
        <li
          v-for="n in items"
          :key="n.id"
          class="vv-bell__item"
          :class="{ 'is-unread': !n.read, 'is-fail': failed(n) }"
          @click="openItem(n)"
        >
          <i class="material-icons vv-bell__ic">{{ icon(n) }}</i>
          <div class="vv-bell__txt">
            <b>{{ n.title }}</b>
            <span v-if="n.body">{{ n.body }}</span>
            <small>{{ fDate(n.createdAt) }}</small>
          </div>
          <span v-if="!n.read" class="vv-bell__dot" :title="$t('notifications.unread')"></span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { apiGet, apiPatch } from "../../api/vivo/http";
import {
  typeMeta,
  badgeText,
  parseCount,
  parseList,
  targetFor,
  isFailure,
  markRead,
  markAllRead,
  decrement,
} from "../../views/vivo/notifications-vm";

const POLL_MS = 60000;

export default {
  name: "NotificationsBell",
  data() {
    return { open: false, count: 0, items: [], loading: false, error: false, busy: false, timer: null };
  },
  computed: {
    badge() {
      return badgeText(this.count);
    },
  },
  mounted() {
    this.refreshCount();
    this.timer = setInterval(this.refreshCount, POLL_MS);
    document.addEventListener("click", this.onDocClick);
  },
  beforeDestroy() {
    clearInterval(this.timer);
    document.removeEventListener("click", this.onDocClick);
  },
  methods: {
    async refreshCount() {
      try {
        this.count = parseCount(await apiGet("/notifications/unread-count"));
      } catch (e) {
        /* the bell is non-critical: keep the last known count */
      }
    },
    async loadItems() {
      this.loading = true;
      this.error = false;
      try {
        this.items = parseList(await apiGet("/notifications", { limit: 20 }));
      } catch (e) {
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
    toggle() {
      this.open = !this.open;
      if (this.open) {
        this.refreshCount();
        this.loadItems();
      }
    },
    onDocClick(ev) {
      if (this.open && this.$refs.root && !this.$refs.root.contains(ev.target)) this.open = false;
    },
    async openItem(n) {
      if (!n.read) await this.read(n);
      const loc = targetFor(n);
      if (loc) {
        this.open = false;
        this.$router.push(loc).catch(() => {});
      }
    },
    async read(n) {
      const wasUnread = !n.read; // captured first: an adapter may mutate the shared row
      try {
        await apiPatch(`/notifications/${encodeURIComponent(n.id)}/read`);
        this.count = decrement(this.count, wasUnread);
        this.items = markRead(this.items, n.id, new Date().toISOString());
      } catch (e) {
        /* leave unread; the user can retry */
      }
    },
    async readAll() {
      this.busy = true;
      try {
        await apiPatch("/notifications/read-all");
        this.count = 0;
        this.items = markAllRead(this.items, new Date().toISOString());
      } catch (e) {
        /* keep state */
      } finally {
        this.busy = false;
      }
    },
    icon(n) {
      return typeMeta(n.type).icon;
    },
    failed(n) {
      return isFailure(n);
    },
    fDate(iso) {
      if (!iso) return "";
      return new Intl.DateTimeFormat(this.$i18n.locale, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        numberingSystem: "latn",
      }).format(new Date(iso));
    },
  },
};
</script>
